// =====================================================================
// Assessment Center — Auth module: MVP provider implementation.
//
// Implements the AuthProvider contract from ./types.ts using the
// in-memory users-store.ts. This is the ONLY file that would be
// deleted/replaced when swapping in Cloudflare Access, Auth.js,
// Clerk, or Supabase Auth — everything that imports AuthProvider
// (AuthContext.tsx and everything above it) stays exactly as is.
// =====================================================================
import type {
  AuthProvider,
  AuthUser,
  InstructorLoginInput,
  InstructorRegisterInput,
  StudentLoginInput,
  StudentRegisterInput,
} from "./types";
import * as usersStore from "./users-store";
import { hashPassword, verifyPassword } from "./password";
import { getSession, setSession, clearSession } from "./session";

function toAuthUser(user: ReturnType<typeof usersStore.getUser>): AuthUser {
  if (!user) throw new Error("User not found.");
  const institution =
    user.role === "teacher" ? usersStore.getTeacher(user.id)?.specialization ?? null : undefined;
  return {
    id: user.id,
    email: user.email,
    fullName: user.full_name,
    role: user.role === "teacher" ? "instructor" : "student",
    institution,
  };
}

export class LocalAuthProvider implements AuthProvider {
  getCurrentUser(): AuthUser | null {
    return getSession();
  }

  async studentLogin(input: StudentLoginInput): Promise<AuthUser> {
    const email = input.email.trim();
    if (!email) throw new Error("Enter your email address.");
    if (!input.fullName.trim()) throw new Error("Enter your full name.");

    const existing = usersStore.findUserByEmail(email, "student");
    if (!existing) {
      throw new Error("No account found for that email. Create a new account to get started.");
    }
    if (existing.status !== "active") {
      throw new Error("This account is not active. Contact your instructor.");
    }
    // Keep the display name in sync with whatever the student typed —
    // there's no password to check on this flow by design (see README.md).
    usersStore.updateUserName(existing.id, input.fullName);
    const authUser = toAuthUser(usersStore.getUser(existing.id));
    setSession(authUser);
    return authUser;
  }

  async studentRegister(input: StudentRegisterInput): Promise<AuthUser> {
    const email = input.email.trim();
    if (!input.fullName.trim()) throw new Error("Enter your full name.");
    if (!email) throw new Error("Enter your email address.");
    if (input.password.length < 6) throw new Error("Password must be at least 6 characters.");
    if (usersStore.findUserByEmail(email, "student")) {
      throw new Error("An account already exists for that email — try logging in instead.");
    }

    const passwordHash = await hashPassword(input.password);
    const user = usersStore.createUser({
      email,
      password_hash: passwordHash,
      full_name: input.fullName,
      role: "student",
    });
    usersStore.createStudentProfile(user.id);

    const authUser = toAuthUser(user);
    setSession(authUser);
    return authUser;
  }

  async instructorLogin(input: InstructorLoginInput): Promise<AuthUser> {
    const email = input.email.trim();
    if (!email || !input.password) throw new Error("Enter your email and password.");

    const existing = usersStore.findUserByEmail(email, "teacher");
    if (!existing) throw new Error("No instructor account found for that email.");
    if (existing.status !== "active") throw new Error("This account is not active.");

    const valid = await verifyPassword(input.password, existing.password_hash);
    if (!valid) throw new Error("Incorrect password.");

    const authUser = toAuthUser(existing);
    setSession(authUser);
    return authUser;
  }

  async instructorRegister(input: InstructorRegisterInput): Promise<AuthUser> {
    const email = input.email.trim();
    if (!input.fullName.trim()) throw new Error("Enter your full name.");
    if (!email) throw new Error("Enter your email address.");
    if (input.password.length < 6) throw new Error("Password must be at least 6 characters.");
    if (usersStore.findUserByEmail(email, "teacher")) {
      throw new Error("An account already exists for that email — try logging in instead.");
    }

    const passwordHash = await hashPassword(input.password);
    const user = usersStore.createUser({
      email,
      password_hash: passwordHash,
      full_name: input.fullName,
      role: "teacher",
    });
    usersStore.createTeacherProfile(user.id, input.institution || null);

    const authUser = toAuthUser(user);
    setSession(authUser);
    return authUser;
  }

  signOut(): void {
    clearSession();
  }
}
